import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { User, OrganizationMember } from '../types';

export const userService = {
  // Create user profile
  async createUserProfile(
    userId: string,
    data: {
      email: string;
      firstName: string;
      lastName: string;
      organizationId?: string;
    }
  ): Promise<void> {
    try {
      console.log('Creating user profile...', { userId, data });
      const userRef = doc(db, 'users', userId);
      
      await setDoc(userRef, {
        ...data,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        organizationId: data.organizationId,
      });
      
      console.log('User profile created successfully');
    } catch (error) {
      console.error('Error in createUserProfile:', error);
      throw error;
    }
  },

  // Find user by email
  async findUserByEmail(email: string): Promise<User | null> {
    try {
      console.log('Searching for user by email...', { email });
      
      // Première tentative : recherche directe par email
      const usersRef = collection(db, 'users');
      const q = query(usersRef, where('email', '==', email.toLowerCase()));
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const userData = querySnapshot.docs[0];
        const user = {
          id: userData.id,
          ...userData.data(),
        } as User;
        console.log('User found by direct email query:', user);
        return user;
      }
      
      // Deuxième tentative : parcourir tous les utilisateurs
      // Cette approche est moins efficace mais peut être nécessaire si l'index sur email n'existe pas
      console.log('User not found by direct query, trying alternative approach...');
      const allUsersSnapshot = await getDocs(usersRef);
      
      for (const userDoc of allUsersSnapshot.docs) {
        const userData = userDoc.data();
        if (userData.email && userData.email.toLowerCase() === email.toLowerCase()) {
          const user = {
            id: userDoc.id,
            ...userData,
          } as User;
          console.log('User found by scanning all users:', user);
          return user;
        }
      }
      
      // Troisième tentative : vérifier dans les membres de l'organisation
      console.log('User not found in users collection, checking organization members...');
      const orgsRef = collection(db, 'organizations');
      const orgsSnapshot = await getDocs(orgsRef);
      
      for (const orgDoc of orgsSnapshot.docs) {
        const membersRef = collection(db, 'organizations', orgDoc.id, 'members');
        const membersSnapshot = await getDocs(membersRef);
        
        for (const memberDoc of membersSnapshot.docs) {
          const memberData = memberDoc.data();
          if (memberData.email && memberData.email.toLowerCase() === email.toLowerCase()) {
            // Nous avons trouvé un membre avec cet email, utilisons son ID pour créer un utilisateur
            const user = {
              id: memberDoc.id,
              email: memberData.email,
              firstName: memberData.firstName || '',
              lastName: memberData.lastName || '',
              displayName: `${memberData.firstName || ''} ${memberData.lastName || ''}`.trim(),
              organizationId: orgDoc.id,
              createdAt: memberData.joinedAt || new Date(),
              updatedAt: memberData.updatedAt || new Date()
            } as unknown as User;
            console.log('User found in organization members:', user);
            return user;
          }
        }
      }

      console.log('User not found after all attempts');
      return null;
    } catch (error) {
      console.error('Error in findUserByEmail:', error);
      throw error;
    }
  },

  // Get user by ID
  async getUserById(userId: string): Promise<User | null> {
    try {
      console.log('Fetching user by ID...', { userId });
      const userRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userRef);

      if (!userDoc.exists()) {
        console.log('User not found');
        return null;
      }

      const user = {
        id: userDoc.id,
        ...userDoc.data(),
      } as User;

      console.log('User fetched successfully:', user);
      return user;
    } catch (error) {
      console.error('Error in getUserById:', error);
      throw error;
    }
  },

  // Get all users in an organization
  async getOrganizationUsers(organizationId: string): Promise<User[]> {
    try {
      console.log('Fetching organization users...', { organizationId });
      
      // Get organization members
      const membersRef = collection(db, 'organizations', organizationId, 'members');
      const membersSnapshot = await getDocs(membersRef);
      
      // Get full user details for each member
      const userPromises = membersSnapshot.docs.map(async (memberDoc) => {
        const userId = memberDoc.id;
        const userDoc = await getDoc(doc(db, 'users', userId));
        
        if (!userDoc.exists()) {
          console.warn('User document not found for member:', userId);
          return null;
        }

        return {
          id: userDoc.id,
          ...userDoc.data(),
        } as User;
      });

      const users = (await Promise.all(userPromises)).filter((user): user is User => user !== null);
      console.log('Organization users fetched successfully:', { count: users.length });
      return users;
    } catch (error) {
      console.error('Error in getOrganizationUsers:', error);
      throw error;
    }
  },

  // Update user organization
  async updateUserOrganization(userId: string, organizationId: string): Promise<void> {
    try {
      console.log('Updating user organization...', { userId, organizationId });
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, { 
        organizationId,
        updatedAt: serverTimestamp()
      });
      console.log('User organization updated successfully');
    } catch (error) {
      console.error('Error in updateUserOrganization:', error);
      throw error;
    }
  },
};
